// Unpublished Work © 2023-2024 Deere & Company.

import {
  Header as FuelHeader,
  HeaderActionArea,
  Popover,
  PopoverButton,
  PopoverContent,
} from "@deere/fuel-react";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import MailIcon from "@mui/icons-material/Mail";
import SettingsIcon from "@mui/icons-material/Settings";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useOktaAuth } from "@okta/okta-react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import Alerts from "./components/AlertsAndNotifications/Alerts/Alerts";
import NotificationV2 from "./components/AlertsAndNotifications/Notifications/Notifications";
import { DealerAccount } from "../../../../api/dealer/types";
import { RBACRole } from "../../../../api/rbac/types";
import xtremeBffApi from "../../../../api/xtremeBff/xtremeBffApi";
import {
  APPLICATION_LINKS_UNIQUE_KEYS,
  LOGOUT_PATH,
} from "../../../../constant";
import { ConfigContext } from "../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../context/DealerAccountContext";
import { ROLES_WITH_DELEGATION_ACCESS } from "../../../../context/DelegateContext/DelegateContext";
import { FeatureToggleContextV2 } from "../../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { RBACAccessContext } from "../../../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../../../context/UserContext/UserContext";
import {
  CATEGORIES,
  TOGGLES,
} from "../../../../pages/featureToggles/constants";
import { ALL_ACCOUNTS } from "../../../../utils/constants";
import { decodeHtmlEntities } from "../../../../utils/formatters";
import { getHomeUrl } from "../../../../utils/legacyHome";
import LocalStorage from "../../../../utils/LocalStorage";
import { getAccountIdFromUrl } from "../../../../utils/urlFormatters";
import useNavigationItems from "../../hooks/useNavigationItems";
import ActiveDealerAccount from "./components/ActiveDealerAccount/ActiveDealerAccount";
import LanguagePref from "./components/LanguagePref";
import MenuButton from "./components/MenuButton/MenuButton";
import MyApplication from "./components/MyApplications/MyApplications";
import NotificationsV1 from "./components/Notifications/Notifications";
import SearchDealerAccount from "./components/SearchDealerAccount/SearchDealerAccount";
import SelectDealerAccount from "./components/SelectDealerAccount/SelectDealerAccount";
import UfcFeedback from "./UfcFeedback";
import { isAuthorizedForMultipleAccounts } from "../../../../pages/preferences/utils";
import GlobalAlertBanner from "../GlobalAlertBanner/GlobalAlertBanner";

function Header() {
  const { authState } = useOktaAuth();
  const {
    JDPO_JAVA_URL,
    JDPOINT_URL,
    ENABLE_PREFERENCES,
    ENABLE_FEATURE_TOGGLES,
    ENABLE_SUPER_ADMIN_FEATURE_TOGGLES,
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_FEATURE_TOGGLE_SERVICE_INTEGRATION,
    ENABLE_DYNAMIC_EXTERNAL_LINKS_FEATURE_TOGGLE,
    ENABLE_REMOVE_DASHBOARD_TAB,
    ENABLE_DELEGATE_LOGIN,
  } = useContext(ConfigContext);
  const { isRBACEnabled, isCorporateViewEnabled } =
    useContext(RBACAccessContext);
  const {
    dealerAccount,
    selectedAccounts,
    mappedAccounts,
    corporateManagerSetupIncomplete,
    updateSelectedAccounts,
    dealerInfoLoaderResponseStatus,
  } = useContext(DealerAccountContext);
  const accountId = dealerAccount?.id as string;
  const user = useContext(UserContext);
  const theme = useTheme();
  const { t } = useTranslation("common");
  const { pathname, search } = useLocation();
  const [showRPM, setShowRPM] = useState<boolean>(false);
  const { XTREME_BFF_SERVICE_URL, ENABLE_RPM_FOR_JDPRISM_LINKS } =
    useContext(ConfigContext);
  const lastVisitedAccountId =
    isRBACEnabled &&
    (LocalStorage.getJSONValue("jdRoleActivity")
      ?.lastVisitedAccountId as string);

  const dealerAccountId =
    user?.is(RBACRole.CORPORATE_PARTS_MANAGER) && selectedAccounts.length > 1
      ? ALL_ACCOUNTS
      : (dealerAccount?.id as string) || getAccountIdFromUrl(pathname, search);

  const { navigationItems } = useNavigationItems(
    dealerAccountId ?? "",
    !!user?.is(RBACRole.CORPORATE_PARTS_MANAGER),
    corporateManagerSetupIncomplete,
    showRPM
  );
  const [isOpenProfile, setIsOpenProfile] = useState(false);
  const [isOpenMyApplication, setIsOpenMyApplication] = useState(false);

  const navigate = useNavigate();
  const homeUrl = getHomeUrl(
    JDPO_JAVA_URL,
    JDPOINT_URL,
    dealerAccountId ?? "",
    ENABLE_REMOVE_DASHBOARD_TAB
  );
  const { isToggleEnabled, additionalFieldsValues } = useContext(
    FeatureToggleContextV2
  );

  useEffect(() => {
    const fetchShowRPM = async () => {
      try {
        const result = await xtremeBffApi.showRPM(
          XTREME_BFF_SERVICE_URL,
          authState?.accessToken?.accessToken as string,
          accountId ?? ""
        );
        setShowRPM(result);
      } catch (error) {
        console.info("Failed to fetch showRPM:", error);
      }
    };

    if (
      authState?.accessToken?.accessToken &&
      accountId &&
      ENABLE_RPM_FOR_JDPRISM_LINKS &&
      isToggleEnabled(CATEGORIES.EXTREME_UI, TOGGLES.ENABLE_RPM_FOR_JDPRISM)
    ) {
      fetchShowRPM();
    }
  }, [XTREME_BFF_SERVICE_URL, authState?.accessToken?.accessToken, accountId]);

  const headerTitle = t("header.title");

  const { ENABLE_MULTIPLE_ACCOUNTS, ENABLE_CORPORATE_VIEW_DPA_ROLE } =
    useContext(ConfigContext);

  const enableAccountSelectionOptions =
    isCorporateViewEnabled &&
    isAuthorizedForMultipleAccounts(
      user,
      ENABLE_MULTIPLE_ACCOUNTS,
      ENABLE_CORPORATE_VIEW_DPA_ROLE
    );

  const showFeatureTogglesMenuItemBasedOnStaticToggles =
    ENABLE_FEATURE_TOGGLES ||
    (ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
      ENABLE_FEATURE_TOGGLE_SERVICE_INTEGRATION);

  const showFeatureTogglesMenuItemBasedOnRBAC =
    isRBACEnabled &&
    (ENABLE_SUPER_ADMIN_FEATURE_TOGGLES
      ? user?.is(RBACRole.JD_SUPER_ADMIN) ||
        user?.is(RBACRole.JD_DEVELOPER_ADMIN)
      : user?.is(RBACRole.JD_ADMIN) || user?.is(RBACRole.JD_DEVELOPER_ADMIN));

  const currentUserHasJDRole = isRBACEnabled && user?.isUserWithJDRole();

  const isValidJDUserWithAccount = currentUserHasJDRole && !!dealerAccountId;

  const showFeatureTogglesMenuItem =
    showFeatureTogglesMenuItemBasedOnRBAC &&
    showFeatureTogglesMenuItemBasedOnStaticToggles;

  const showManageBroadcastMessageMenuItem = currentUserHasJDRole;

  const showDelegateLogonMenuItem =
    ENABLE_DELEGATE_LOGIN &&
    isRBACEnabled &&
    ROLES_WITH_DELEGATION_ACCESS.some((role) => user?.is(role));

  const isInvalidAccountMapped = useMemo(
    () =>
      isRBACEnabled &&
      isCorporateViewEnabled &&
      user?.is(RBACRole.CORPORATE_PARTS_MANAGER) &&
      mappedAccounts.includes(undefined as unknown as DealerAccount),
    [mappedAccounts]
  );

  const menuButtonOnClickHandler = (navigationURL: string) => {
    navigate(navigationURL);
  };

  const showNonGlobalFeatureLinks =
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
    !(
      ENABLE_DYNAMIC_EXTERNAL_LINKS_FEATURE_TOGGLE &&
      isToggleEnabled(CATEGORIES.EXTREME_UI, TOGGLES.ENABLE_EXTERNAL_LINKS)
    );

  const selectedExternalLinksMap = useMemo(
    () =>
      additionalFieldsValues(
        CATEGORIES.EXTREME_UI,
        TOGGLES.ENABLE_EXTERNAL_LINKS
      ),
    [
      additionalFieldsValues,
      CATEGORIES.EXTREME_UI,
      TOGGLES.ENABLE_EXTERNAL_LINKS,
    ]
  );

  const selectedExternalLinks =
    selectedExternalLinksMap?.get("external_links") || [];

  const externalLinksVisibility = new Map<string, boolean>(
    APPLICATION_LINKS_UNIQUE_KEYS.map((key) => [
      key,
      selectedExternalLinks.includes(key),
    ])
  );

  const getAccountDetails = () => {
    if (isCorporateViewEnabled && !user) return null;
    if (enableAccountSelectionOptions && !isInvalidAccountMapped) {
      return (
        <SelectDealerAccount
          mappedAccounts={mappedAccounts}
          activeAccounts={selectedAccounts}
          onChange={updateSelectedAccounts}
        />
      );
    }
    if (
      isValidJDUserWithAccount ||
      (isRBACEnabled &&
        (!dealerInfoLoaderResponseStatus ||
          (dealerInfoLoaderResponseStatus === 404 && lastVisitedAccountId)))
    ) {
      return (
        <SearchDealerAccount
          accountId={dealerAccountId}
          hideSearchButton
          styleSx={{
            height: "24px",
            width: "300px",
          }}
        />
      );
    }
    if (!currentUserHasJDRole && !isInvalidAccountMapped)
      return (
        <ActiveDealerAccount
          accountId={dealerAccountId as string}
          dealerName={dealerAccount?.name as string}
        />
      );
    return null;
  };

  function isPreferenceEnabled() {
    if (!user) {
      return false;
    }
    const isCorporateViewOnly =
      Array.isArray(user.roles) &&
      user.roles.length === 1 &&
      (user.roles[0] === RBACRole.CORPORATE_VIEW ||
        user?.is(RBACRole.CORPORATE_VIEW));

    if (isCorporateViewOnly) {
      return false;
    }
    return ENABLE_PREFERENCES;
  }

  const performLogout = () => {
    navigate(LOGOUT_PATH);
  };

  const isAppNotificationsEnabled = isToggleEnabled(
    CATEGORIES.ALERTS_SERVICE,
    TOGGLES.ENABLE_ALERTS_AND_NOTIFICATIONS
  );

  return (
    <div data-testid="parts-header">
      <FuelHeader
        logoLink={homeUrl}
        // titleLink={homeUrl}
        navigationItems={navigationItems}
        title={headerTitle}
      >
        <HeaderActionArea>
          {isPreferenceEnabled() && <LanguagePref />}
          <UfcFeedback />
          <MyApplication
            isOpen={isOpenMyApplication}
            onClose={() => setIsOpenMyApplication(false)}
            onToggle={() => setIsOpenMyApplication((prev) => !prev)}
            theme={theme}
            t={t}
            showNonGlobalFeatureLinks={showNonGlobalFeatureLinks}
            externalLinksVisibility={externalLinksVisibility}
          />
          {isAppNotificationsEnabled && <Alerts />}
          {isAppNotificationsEnabled && <NotificationV2 />}
          {!isAppNotificationsEnabled && dealerAccountId && (
            <NotificationsV1
              accountId={dealerAccountId}
              key={dealerAccountId}
            />
          )}
          {authState?.accessToken && (
            <Popover
              showClose={false}
              isOpen={isOpenProfile}
              onClose={() => setIsOpenProfile(false)}
            >
              <PopoverButton
                data-testid="profile-button"
                aria-label="Account"
                variant="text"
                sx={{
                  ":hover": {
                    background: theme.tokens.FuelColorGray50,
                  },
                  ":active": {
                    background:
                      theme.tokens.FuelColorBackgroundActionSecondaryPressed,
                  },
                }}
                onClick={() => setIsOpenProfile((prev) => !prev)}
              >
                <AccountCircleOutlinedIcon
                  sx={{
                    color: theme.tokens.FuelColorTextIcon,
                  }}
                  data-testid="profile-icon"
                />
              </PopoverButton>
              <PopoverContent>
                <Box minWidth="200px">
                  <Typography
                    data-testid="first-name"
                    sx={{
                      fontWeight: 600,
                      p: 2,
                      pl: 5,
                      height: "40px",
                      fontSize: "14px",
                      color: theme.tokens.FuelColorTextTertiary,
                    }}
                  >
                    {t("header.userActionMenu.greeting", {
                      username: decodeHtmlEntities(
                        authState?.accessToken?.claims?.firstName as string
                      ),
                    })}
                  </Typography>
                </Box>
                <Divider />
                {isPreferenceEnabled() && (
                  <MenuButton
                    onClick={() => {
                      menuButtonOnClickHandler(`/preferences`);
                      setIsOpenProfile(false);
                    }}
                    menuIcon={{
                      Component: TuneIcon,
                    }}
                    featureName="preferences"
                    displayText={t("header.userActionMenu.preferencesLink")}
                  />
                )}
                {showFeatureTogglesMenuItem && (
                  <MenuButton
                    onClick={() => {
                      menuButtonOnClickHandler(`/feature-toggles`);
                      setIsOpenProfile(false);
                    }}
                    menuIcon={{
                      Component: SettingsIcon,
                    }}
                    featureName="feature-toggles"
                    displayText={t("header.userActionMenu.featureTogglesLink")}
                  />
                )}
                {showManageBroadcastMessageMenuItem && (
                  <MenuButton
                    onClick={() => {
                      menuButtonOnClickHandler(`/broadcast-messages`);
                      setIsOpenProfile(false);
                    }}
                    menuIcon={{
                      Component: MailIcon,
                    }}
                    featureName="manage-broadcast-messages"
                    displayText={t(
                      "header.userActionMenu.manageBroadcastMessagesLink"
                    )}
                  />
                )}
                {showDelegateLogonMenuItem && (
                  <MenuButton
                    onClick={() => {
                      menuButtonOnClickHandler(`/delegate-logon`);
                      setIsOpenProfile(false);
                    }}
                    menuIcon={{
                      Component: SwitchAccountIcon,
                    }}
                    featureName="delegate-logon"
                    displayText={t("header.userActionMenu.delegateLogonLink")}
                  />
                )}
                <Box minWidth="200px">
                  <ListItemButton
                    sx={{
                      pl: 5,
                      height: "40px",
                    }}
                    data-testid="logout-button"
                    onClick={() => performLogout()}
                  >
                    <ListItemIcon sx={{ pr: "8px", minWidth: "0" }}>
                      <LogoutIcon
                        data-testid="logout-icon"
                        sx={{
                          color: theme.tokens.FuelColorTextIcon,
                          width: "24px",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      data-testid="logout"
                      primary={t("header.userActionMenu.logout")}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: theme.tokens.FuelColorTextHeading,
                      }}
                    />
                  </ListItemButton>
                </Box>
              </PopoverContent>
            </Popover>
          )}
        </HeaderActionArea>
        <Box
          data-testid="account-section"
          sx={{
            display: "flex",
            width: "fit-content",
            alignItems: "center",
            color: theme.tokens.FuelColorAlertInfoIcon,
            backgroundColor: theme.tokens.FuelColorAlertInfoBackground,
            gap: "4px",
            borderRadius: "30px",
            position: "absolute",
            right: "0.8%",
            top: "120%",
            paddingX: "9.5px",
            ...(enableAccountSelectionOptions || currentUserHasJDRole
              ? {
                  width: "300px",
                  minWidth: "300px",
                  top: "106%",
                  backgroundColor: "transparent",
                  borderRadius: 0,
                  paddingX: 0,
                }
              : {}),
          }}
        >
          {getAccountDetails()}
        </Box>
      </FuelHeader>
      <GlobalAlertBanner />
    </div>
  );
}

export default Header;
